import React from 'react';
import { FormStateProvider, useFormState } from './useFormState';
import { useGetOwnerExperienceQuery } from '../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { useParams } from 'react-router';
import StepRouter from './StepRouter';
import {
    Box,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import StepForm from './StepForm';
import { camelCaseToTitleCase } from '../../../../shared/utils';

const ProgressBar = () => {
    const theme = useTheme();
    const { step, steps, stepIndex } = useFormState();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const current = stepIndex + 1;

    const nextStep = steps[stepIndex + 1] ?? 'review';
    const total = steps.length;

    return isMobile ? (
        <Stack
            spacing={1}
            sx={{
                position: 'sticky',
                top: 0,
                my: 2,
                zIndex: 1000,
                bgcolor: 'background.default',
                py: 2,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    Step {current} of {total}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Next: {camelCaseToTitleCase(nextStep)}
                </Typography>
            </Box>
            <Stack
                direction='row'
                spacing={0.5}
                role='presentation'
                aria-label='Form progress'
                aria-valuemin={1}
                aria-valuemax={total}
                aria-valuenow={current}
                aria-valuetext={`Step ${current} of ${total}: ${camelCaseToTitleCase(step)}`}
            >
                {steps.map((item, index) => (
                    <Box
                        key={item}
                        sx={{
                            flex: 1,
                            height: 4,
                            borderRadius: 1,
                            bgcolor:
                                index <= stepIndex
                                    ? 'primary.main'
                                    : 'action.disabledBackground',
                        }}
                    />
                ))}
            </Stack>
        </Stack>
    ) : (
        <Stepper activeStep={stepIndex} sx={{ my: 4 }}>
            {steps.map((step) => (
                <Step key={step}>
                    <StepLabel>{camelCaseToTitleCase(step)}</StepLabel>
                </Step>
            ))}
        </Stepper>
    );
};

const StepHandler = () => {
    const { step } = useFormState();

    return (
        <StepForm key={step}>
            <StepRouter />
        </StepForm>
    );
};

const ExperienceForm = () => {
    const loggedInUser = UserRole();
    const { id } = useParams();

    const { data: experience } = useGetOwnerExperienceQuery(
        { owner: loggedInUser?.company, experience: Number(id) },
        {
            skip: !id,
        },
    );

    if (!experience) return <LoadingComponent />;

    return (
        <FormStateProvider experience={experience}>
            <Typography variant='h1'>
                {experience.name ??
                    `Your ${camelCaseToTitleCase(experience.details.subType)}`}
            </Typography>
            <ProgressBar />
            <StepHandler />
        </FormStateProvider>
    );
};

export default ExperienceForm;
